import { useCallback, useMemo } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { DividerLine } from 'src/core/ui';
import { calculatePercentile } from 'src/utils/arrayUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { CompListing } from 'src/WebApiController';

import * as styles from './SelectedListingMapStats.css';
import { StatLabel } from './StatLabel';

export interface ListingMapStats {
  listingCount: number;
  ticketCount: number;
  ticketPriceMin: number;
  ticketPriceP25: number;
  ticketPriceAvg: number;
}

export const SelectedListingMapStats = ({
  currencyCode,
  compListings,
}: {
  currencyCode?: string | null;
  compListings?: CompListing[];
}) => {
  const listingQtyText = useContent(ContentId.ListingQty);
  const ticketQtyText = useContent(ContentId.TicketQty);
  const minText = useContent(ContentId.Min);
  const p25Text = useContent(ContentId.P25);
  const p50Text = useContent(ContentId.P50);

  const stats = useMemo(() => {
    const listingCount = (compListings ?? []).length;
    let ticketCount = 0;
    const ticketPrices: number[] = [];
    for (const listing of compListings ?? []) {
      if ((listing.availableTickets ?? 0) > 0) {
        ticketCount += listing.availableTickets ?? 0;
        const prices = Array(listing.availableTickets ?? 0).fill(
          listing.sellerAllInPrice?.amt ?? 0
        );
        ticketPrices.push(...prices);
      }
    }
    return {
      listingCount,
      ticketCount,
      ticketPriceMin: calculatePercentile(ticketPrices, 0),
      ticketPriceP25: calculatePercentile(ticketPrices, 25),
      ticketPriceAvg: calculatePercentile(ticketPrices, 50),
    };
  }, [compListings]);

  const formatPrice = useCallback(
    (price: number) => {
      return applyNumberFormatting({
        inputNumber: price,
        currencyCode,
        currencyDecimalDigits: 2,
      });
    },
    [currencyCode]
  );

  return (
    <div className={styles.statsContainer}>
      <StatLabel
        stat={stats.listingCount.toLocaleString()}
        title={listingQtyText}
      />
      <DividerLine vertical height={'100%'} />
      <StatLabel
        stat={stats.ticketCount.toLocaleString()}
        title={ticketQtyText}
      />
      <DividerLine vertical height={'100%'} />
      <StatLabel
        stat={formatPrice(stats.ticketPriceMin) ?? ''}
        title={minText}
      />
      <DividerLine vertical height={'100%'} />
      <StatLabel
        stat={formatPrice(stats.ticketPriceP25) ?? ''}
        title={p25Text}
      />
      <DividerLine vertical height={'100%'} />
      <StatLabel
        stat={formatPrice(stats.ticketPriceAvg) ?? ''}
        title={p50Text}
      />
    </div>
  );
};
